/* A contact's channel types (CT-2) — what the Contacts table and the contact modal share: the seven
   types the API takes, their glyph (spec/02's icon map), their label key, and where a value links.

   No brand glyph exists in @ant-design/icons for Telegram or Viber, so they take a neutral one and
   always carry the type's NAME (tooltip + accessible name): the glyph alone is never the label.
   A value is stored as the user typed it — a phone is NOT normalised to E.164. Phone and Viber dial
   (`tel:`, with everything but digits and `+` stripped from the link only; the text shows as typed),
   an e-mail mails, a website opens in a new browser tab; the others are text. */

import { EllipsisOutlined, GlobalOutlined, InstagramOutlined, MailOutlined, MessageOutlined, PhoneOutlined, SendOutlined } from "@ant-design/icons";

export const CHANNEL_TYPES = ["phone", "email", "viber", "telegram", "instagram", "website", "other"];

export const CHANNEL_ICON = {
  phone: PhoneOutlined,
  email: MailOutlined,
  instagram: InstagramOutlined,
  telegram: SendOutlined,
  viber: MessageOutlined,
  website: GlobalOutlined,
  other: EllipsisOutlined,
};

export const channelLabelKey = type => `ct_type_${CHANNEL_TYPES.includes(type) ? type : "other"}`;

/* The same channel listed twice — on one contact, or on two contacts of one counterparty
   (deactivated ones included) — is a WARNING, never a refusal (PICSELL-1603 CT-1/CT-2): the API
   takes any list, and a shared office phone is legitimate. Only a channel added or changed in this
   edit is checked, so a repeat already stored never flags an edit that leaves it alone, and a
   channel is never compared with itself. Only channels of the SAME type are compared — a phone and
   a Viber on one number are two channels.
   Phone / Viber compare as digits with leading zeros dropped, and match when the longer ends with the
   shorter and the shorter has at least `MIN_PHONE_DIGITS` — so "+380671112233", "0671112233" and
   "067 111-22-33" are one number, while two short codes are never matched. E-mail compares
   case-insensitively, the rest after a trim. The stored value is never rewritten. */
const PHONE_LIKE = ["phone", "viber"];
const MIN_PHONE_DIGITS = 7;

const matchValue = channel => {
  const value = String(channel.value || "");
  if (PHONE_LIKE.includes(channel.channel_type)) return value.replace(/\D/g, "").replace(/^0+/, "");
  if (channel.channel_type === "email") return value.trim().toLowerCase();
  return value.trim();
};

const sameValue = (type, a, b) => {
  if (!PHONE_LIKE.includes(type)) return a === b;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= MIN_PHONE_DIGITS && long.endsWith(short);
};

/** Whether two channels are the same one; an empty value matches nothing. */
const sameChannel = (a, b) => {
  if (a.channel_type !== b.channel_type) return false;
  const [x, y] = [matchValue(a), matchValue(b)];
  return !!x && !!y && sameValue(a.channel_type, x, y);
};

/** The `key`s of the second and every later repeat of a channel within one contact. A pair counts
    only when `isChanged` holds for at least one of its two rows. */
export function duplicateChannelKeys(channels, isChanged = () => true) {
  const repeats = new Set();
  channels.forEach((channel, index) => {
    const earlier = channels.slice(0, index).some(other => (isChanged(channel) || isChanged(other)) && sameChannel(channel, other));
    if (earlier) repeats.add(channel.key);
  });
  return repeats;
}

/** Every one of `contacts` that already lists `channel`, in their order; [] when none does. */
export function channelOwners(channel, contacts) {
  return contacts.filter(contact => (contact.channels || []).some(other => sameChannel(channel, other)));
}

/** Where a channel's value links, or null for plain text. */
export function channelHref(channel) {
  const value = String(channel.value || "").trim();
  if (!value) return null;
  if (channel.channel_type === "phone" || channel.channel_type === "viber") return `tel:${value.replace(/[^\d+]/g, "")}`;
  if (channel.channel_type === "email") return `mailto:${value}`;
  if (channel.channel_type === "website") return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  return null;
}
